import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import Icon from '@src/v3/components/coreUI/icon';

// Standard modal sizes
const modalSizes = {
  sm: 'max-w-sm sm:max-w-[400px] max-h-[350px]',
  md: 'max-w-full xs:max-w-md sm:max-w-[550px] max-h-full xs:max-h-[600px]',
  lg: 'max-w-[800px] max-h-[700px]',
  xl: 'max-w-[1100px] max-h-[900px]',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
} as const;

type ModalSize = keyof typeof modalSizes;

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={`
        data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed] :fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[70] bg-[rgba(0,0,0,0.21)] backdrop-blur-[15px] flex flex-col focus:outline-none focus-visible:outline-none outline-offset-0 outline-transparent
        ${className}
      `}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = 'md',
  title,
  header,
  closeOnOutsideClick = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  size?: ModalSize;
  title?: React.ReactNode;
  header?: React.ReactNode;
  overlayClassName?: string;
  closeOnOutsideClick?: boolean;
}) {
  // Check if className contains a height class (h-*, !h-*, min-h-*, max-h-*)
  const hasHeightClass = className?.match(
    /(?:^|\s)(!?h-\[|!?h-\d+|!?min-h-|!?max-h-)/
  );
  const heightClass = hasHeightClass ? '' : 'h-full';

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogTitle className="sr-only">{title || 'Dialog'}</DialogTitle>
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={`
          bg-white 
          fixed z-[70] w-full ${heightClass} xs:rounded-3xl
          shadow-2xl duration-300 focus:outline-none focus-visible:outline-none 
          flex flex-col outline-offset-0 outline-transparent p-5 xs:p-8 pb-0 xs:pb-8
      
          bottom-0 left-0 xs:top-[50%] xs:left-[50%]
          xs:translate-x-[-50%] xs:translate-y-[-50%]
      
          data-[state=open]:animate-in
          data-[state=closed]:animate-out
      
          data-[state=open]:slide-in-from-bottom
          data-[state=closed]:slide-out-to-bottom
      
          xs:data-[state=open]:fade-in-0 
          xs:data-[state=closed]:fade-out-0
          xs:data-[state=open]:zoom-in-95
          xs:data-[state=closed]:zoom-out-95
          xs:data-[state=closed]:slide-out-to-top-[48%]
          xs:data-[state=closed]:slide-out-to-left-1/2
          xs:data-[state=open]:slide-in-from-top-[48%]
          xs:data-[state=open]:slide-in-from-left-1/2
      
          ${modalSizes[size]}
          ${className || ''}
        `}
        onInteractOutside={(e) => {
          if (!closeOnOutsideClick) {
            e.preventDefault();
          }
        }}
        {...props}
      >
        {(title || header) && (
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {header && <div className="dialog-header-content">{header}</div>}
          </DialogHeader>
        )}
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="focus-visible:outline-none ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-full hover:opacity-50 transition-opacity focus:ring-0 focus:ring-offset-none focus:outline-hidden absolute sm:top-8 sm:right-8 top-5 right-5 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5"
          >
            <Icon
              iconName="crossicon"
              iconProps={{ className: 'w-5 h-5 text-gray-900' }}
            />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={`flex items-center justify-between ${className}`}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={`flex flex-col-reverse gap-2 sm:flex-row sm:justify-end mt-auto ${className}`}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={`text-2xl leading-tight font-bold text-gray-900 ${className}`}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={`text-gray-600 text-base ${className}`}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  modalSizes,
};

export type { ModalSize };
